import type { SchemaPath } from '@angular/forms/signals';
import { maxDateError, validate } from '@angular/forms/signals';

/**
 * Parse an ISO-ish date string (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`) into a local
 * `Date` at the start of the coarsest unit present. Returns `null` when the
 * string is empty or unparseable.
 */
export function parseIsoDateString(
  value: string | null | undefined
): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  if (!Number.isFinite(year)) {
    return null;
  }
  const month = match[2] ? Number(match[2]) - 1 : 0;
  const day = match[3] ? Number(match[3]) : 1;
  const parsed = new Date(year, month, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Schema fragment that rejects an ISO-string date field after `maxDate`.
 *
 * Form models keep dates as ISO strings (see `idt-date-picker`), so Angular's
 * built-in `maxDate()` — which only accepts `Date | null` paths — cannot be
 * applied directly. This validator parses the string and emits the same
 * `maxDate` error kind via {@link maxDateError}, so `getError('maxDate')`
 * works in templates and computeds.
 *
 * @param path Schema path of the ISO-string date field.
 * @param maxDate Absolute max, or a thunk evaluated on each validation pass.
 * @example
 * form(model, (p) => {
 *   isoMaxDateValidator(p.creation_date, () => new Date());
 * });
 */
export function isoMaxDateValidator(
  path: SchemaPath<string>,
  maxDate: Date | (() => Date)
): void {
  validate(path, (ctx) => {
    const parsed = parseIsoDateString(ctx.value());
    if (!parsed) {
      return null;
    }
    const max = typeof maxDate === 'function' ? maxDate() : maxDate;
    return parsed.getTime() > max.getTime() ? maxDateError(max) : null;
  });
}
